/**
 * Realtime WS client (T-037 R2 · W3-CONTRACTS §7 · D-026 ยกเลิก polling)
 * `useTicketSocket(ticketId)` → wss?://<same origin>/ws/tickets/<id>?since=<seq>
 *   - reconnect backoff 1→30s (double ทุกครั้ง, cap 30s)
 *   - `since` = seq ล่าสุดที่ได้ (reconnect ไม่พลาด event)
 *   - `ticket_event` → invalidate การ์ด + list
 *   - `presence`/`typing` → เก็บ viewers/typing ไว้ให้ PresenceBar/Composer ใช้
 *   - ต่อไม่ได้ 3 ครั้งติด → `offline=true` (แสดง badge; query ที่มี refetchInterval แบบ D-026 เดิมจะ poll กลับมาเอง)
 * `useHubSocket()` → /ws/me → notification/badge → invalidate notifications, ticket_updated → invalidate การ์ด/list
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { HubMessage, PresenceState, PresenceViewer, RoomMessage } from "@uwu/contracts";
import { keys } from "./queries";

const MAX_BACKOFF_MS = 30_000;
const MIN_BACKOFF_MS = 1_000;
const OFFLINE_AFTER_ATTEMPTS = 3;

function wsUrl(path: string): string {
  const proto = typeof window !== "undefined" && window.location.protocol === "https:" ? "wss:" : "ws:";
  const host = typeof window !== "undefined" ? window.location.host : "";
  return `${proto}//${host}${path}`;
}

function backoffMs(attempt: number): number {
  return Math.min(MAX_BACKOFF_MS, MIN_BACKOFF_MS * 2 ** Math.max(0, attempt - 1));
}

/** สุขภาพ WS ของการ์ดปัจจุบัน — ให้ queries.ts ปิด polling ตอน WS ทำงานอยู่ (D-026) */
const ticketHealthy = new Map<string, boolean>();
export function isTicketRealtimeHealthy(ticketId: string): boolean {
  return ticketHealthy.get(ticketId) === true;
}
function setTicketHealthy(ticketId: string, healthy: boolean): void {
  if (healthy) ticketHealthy.set(ticketId, true);
  else ticketHealthy.delete(ticketId);
}

let hubHealthyFlag = false;
export function isHubRealtimeHealthy(): boolean {
  return hubHealthyFlag;
}

export type TicketSocketState = {
  connected: boolean;
  offline: boolean;
  viewers: PresenceViewer[];
  /** userId → typing state (ไม่รวม "viewing") */
  typing: Map<string, PresenceState>;
  sendPresence: (state: PresenceState) => void;
};

function safeParseRoomMessage(data: unknown): RoomMessage | null {
  if (typeof data !== "string") return null;
  try {
    const parsed = JSON.parse(data) as unknown;
    const r = RoomMessageLoose(parsed);
    return r;
  } catch {
    return null;
  }
}

/** parse แบบยืดหยุ่น (ไม่ผูก zod กลางทาง hot path) — ตรวจ discriminant เอง กัน WS message แปลกปลอม */
function RoomMessageLoose(v: unknown): RoomMessage | null {
  if (!v || typeof v !== "object" || !("type" in v)) return null;
  const t = (v as { type?: unknown }).type;
  if (t === "ticket_event" || t === "presence" || t === "typing") return v as RoomMessage;
  return null;
}
function HubMessageLoose(v: unknown): HubMessage | null {
  if (!v || typeof v !== "object" || !("type" in v)) return null;
  const t = (v as { type?: unknown }).type;
  if (t === "notification" || t === "badge" || t === "ticket_updated") return v as HubMessage;
  return null;
}

export type TicketSocketOptions = {
  /** [T-058] เรียกเมื่อมี ticket_event ใหม่ (ใช้แจ้ง "การ์ดถูกแก้ระหว่างที่คุณพิมพ์") */
  onTicketEvent?: () => void;
};

export function useTicketSocket(ticketId: string | undefined, opts?: TicketSocketOptions): TicketSocketState {
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const [offline, setOffline] = useState(false);
  const [viewers, setViewers] = useState<PresenceViewer[]>([]);
  const [typing, setTyping] = useState<Map<string, PresenceState>>(new Map());
  const wsRef = useRef<WebSocket | null>(null);
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const seqRef = useRef(0);
  const attemptRef = useRef(0);
  const closedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!ticketId) return undefined;
    closedRef.current = false;
    attemptRef.current = 0;
    seqRef.current = 0;
    setViewers([]);
    setTyping(new Map());
    setOffline(false);

    function connect() {
      if (closedRef.current || !ticketId) return;
      let ws: WebSocket;
      try {
        ws = new WebSocket(wsUrl(`/ws/tickets/${encodeURIComponent(ticketId)}?since=${seqRef.current}`));
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => {
        attemptRef.current = 0;
        setConnected(true);
        setOffline(false);
        setTicketHealthy(ticketId, true);
      };
      ws.onmessage = (ev) => {
        const msg = safeParseRoomMessage(ev.data as unknown);
        if (!msg) return;
        if (msg.type === "ticket_event") {
          if (msg.seq > seqRef.current) seqRef.current = msg.seq;
          optsRef.current?.onTicketEvent?.();
          void qc.invalidateQueries({ queryKey: keys.ticket(ticketId) });
          void qc.invalidateQueries({ queryKey: ["tickets"] });
        } else if (msg.type === "presence") {
          setViewers(msg.viewers);
          setTyping(() => {
            const next = new Map<string, PresenceState>();
            for (const v of msg.viewers) if (v.state !== "viewing") next.set(v.userId, v.state);
            return next;
          });
        } else if (msg.type === "typing") {
          setTyping((prev) => {
            const next = new Map(prev);
            if (msg.state === "viewing") next.delete(msg.user_id);
            else next.set(msg.user_id, msg.state);
            return next;
          });
        }
      };
      ws.onclose = () => {
        setConnected(false);
        setTicketHealthy(ticketId, false);
        wsRef.current = null;
        if (closedRef.current) return;
        scheduleReconnect();
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          /* already closing */
        }
      };
    }

    function scheduleReconnect() {
      attemptRef.current += 1;
      if (attemptRef.current >= OFFLINE_AFTER_ATTEMPTS) setOffline(true);
      timerRef.current = setTimeout(connect, backoffMs(attemptRef.current));
    }

    connect();
    return () => {
      closedRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      wsRef.current?.close();
      wsRef.current = null;
      setTicketHealthy(ticketId, false);
    };
  }, [ticketId, qc]);

  const sendPresence = useCallback((state: PresenceState) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "presence", state }));
    }
  }, []);

  return { connected, offline, viewers, typing, sendPresence };
}

export type HubSocketState = { connected: boolean; offline: boolean };

export function useHubSocket(enabled: boolean): HubSocketState {
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const [offline, setOffline] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const closedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!enabled) return undefined;
    closedRef.current = false;
    attemptRef.current = 0;

    function connect() {
      if (closedRef.current) return;
      let ws: WebSocket;
      try {
        ws = new WebSocket(wsUrl("/ws/me"));
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => {
        attemptRef.current = 0;
        setConnected(true);
        setOffline(false);
        hubHealthyFlag = true;
      };
      ws.onmessage = (ev) => {
        const msg = HubMessageLoose(safeJson(ev.data as unknown));
        if (!msg) return;
        if (msg.type === "notification" || msg.type === "badge") {
          void qc.invalidateQueries({ queryKey: keys.notifications });
        } else if (msg.type === "ticket_updated") {
          void qc.invalidateQueries({ queryKey: keys.ticket(msg.ticket_id) });
          void qc.invalidateQueries({ queryKey: ["tickets"] });
        }
      };
      ws.onclose = () => {
        setConnected(false);
        hubHealthyFlag = false;
        wsRef.current = null;
        if (closedRef.current) return;
        scheduleReconnect();
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          /* already closing */
        }
      };
    }
    function scheduleReconnect() {
      attemptRef.current += 1;
      if (attemptRef.current >= OFFLINE_AFTER_ATTEMPTS) setOffline(true);
      timerRef.current = setTimeout(connect, backoffMs(attemptRef.current));
    }
    connect();
    return () => {
      closedRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      wsRef.current?.close();
      wsRef.current = null;
      hubHealthyFlag = false;
    };
  }, [enabled, qc]);

  return { connected, offline };
}

function safeJson(data: unknown): unknown {
  if (typeof data !== "string") return null;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

/** debounce ง่าย ๆ สำหรับ presence ของ composer (focus/blur → ส่งหลัง 2s) — T-037 R2 */
export function useDebouncedPresence(sendPresence: (state: PresenceState) => void, delayMs = 2000) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const send = useCallback(
    (state: PresenceState) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => sendPresence(state), delayMs);
    },
    [sendPresence, delayMs],
  );
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);
  return send;
}
